import { geoApi } from '@/features/api/geo'
import { useLicence } from '@/features/licence/hooks/useLicence'
import {
    useFormAddressField,
    useFormClearAddressErrors,
    useFormSetAddressField,
    useFormTriggerAddressField,
} from '@/features/pos/components/CustomerProfile/form'
import { AddressInput } from '@/features/pos/components/CustomerProfile/form/types'
import { useAddressSuggestions } from '@/features/pos/components/CustomerProfile/hooks/useAddressSuggestions'
import { useCustomerLookupAddressOptions } from '@/features/pos/components/CustomerProfile/hooks/useCustomerLookupAddressOptions'
import { useLocationEvaluation } from '@/features/pos/components/CustomerProfile/hooks/useLocationEvaluation'
import {
    CustomerLookupAddressOption,
    CustomerLookupAddressSection,
    CustomerLookupCustomer,
    CustomerLookupSaveInput,
} from '@/features/pos/components/CustomerProfile/types'
import { CUSTOMER_SEARCH_DEBOUNCE_MS } from '@/features/pos/components/CustomerProfile/utils'
import { formatCustomerAddress } from '@/features/pos/components/CustomerProfile/utils/transforms'
import { logger } from '@/features/pos/logger'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TextInput } from 'react-native'
import { useDebounce } from 'use-debounce'

interface UseCustomerLookupAddressStateArgs {
    requiredAddress: boolean
    selectedCustomer?: CustomerLookupCustomer
}

interface UseCustomerLookupAddressStateResult {
    addressInputRef: React.RefObject<TextInput | null>
    addressQuery: string
    addressSections: CustomerLookupAddressSection[]
    handleAddressQueryChange: (value: string) => void
    handleAddressSelect: (addressId: string) => Promise<void>
    handleCustomerAddressSelect: (customer: CustomerLookupCustomer) => Promise<void>
    hasAddressSearch: boolean
    resetAddressLookup: () => void
    selectedAddress?: CustomerLookupSaveInput['address']
}

const createUnvalidatedAddress = (address: string): AddressInput => ({
    address,
    coordinates: null,
    isValid: null,
    zone: null,
})

const getCurrentAddress = (
    address: string,
    coordinates: AddressInput['coordinates']
): Pick<CustomerLookupAddressOption, 'address' | 'coordinates'> | undefined => {
    if (!address.trim()) return undefined

    return {
        address,
        coordinates,
    }
}

export const useCustomerLookupAddressState = ({
    requiredAddress,
    selectedCustomer,
}: UseCustomerLookupAddressStateArgs): UseCustomerLookupAddressStateResult => {
    const { country } = useLicence()
    const [addressField, onAddressFieldChange] = useFormAddressField()
    const setAddressField = useFormSetAddressField()
    const clearAddressErrors = useFormClearAddressErrors()
    const triggerAddressField = useFormTriggerAddressField()
    const [isManualAddressSearch, setIsManualAddressSearch] = useState(false)
    const [currentAddress, setCurrentAddress] = useState(() =>
        getCurrentAddress(addressField.address, addressField.coordinates)
    )
    const addressInputRef = useRef<TextInput>(null)
    const addressResolutionIdRef = useRef(0)
    const wasAddressRequiredRef = useRef(requiredAddress)
    const addressQuery = addressField.address
    const [debouncedAddress] = useDebounce(addressQuery, CUSTOMER_SEARCH_DEBOUNCE_MS)
    const evaluateLocation = useLocationEvaluation()

    const selectedAddress = useMemo<CustomerLookupSaveInput['address']>(() => {
        if (addressField.isValid !== true || !addressField.coordinates || !addressField.zone) {
            return undefined
        }

        return {
            address: addressField.address,
            coordinates: addressField.coordinates,
            zone: addressField.zone,
        }
    }, [addressField])

    const hasAddressSearch = isManualAddressSearch && debouncedAddress.trim().length >= 3
    const addressSuggestions = useAddressSuggestions(debouncedAddress, !hasAddressSearch)
    const { addressOptions, addressSections } = useCustomerLookupAddressOptions({
        addressSuggestions,
        currentAddress,
        debouncedAddress,
        hasAddressSearch,
        selectedCustomer,
    })

    useEffect(() => {
        const wasAddressRequired = wasAddressRequiredRef.current

        wasAddressRequiredRef.current = requiredAddress

        if (requiredAddress) {
            const hasAddressDraft =
                !!addressField.address.trim() ||
                addressField.coordinates != null ||
                addressField.isValid != null ||
                addressField.zone != null

            if (!wasAddressRequired && hasAddressDraft) triggerAddressField()

            return
        }

        setIsManualAddressSearch(false)
        clearAddressErrors()
    }, [
        addressField.address,
        addressField.coordinates,
        addressField.isValid,
        addressField.zone,
        clearAddressErrors,
        requiredAddress,
        triggerAddressField,
    ])

    useEffect(
        () => () => {
            addressResolutionIdRef.current += 1
        },
        []
    )

    const resolveAddressSelection = async (
        option: CustomerLookupAddressOption
    ): Promise<AddressInput> => {
        const unvalidatedAddress = createUnvalidatedAddress(option.address)

        if (option.coordinates) {
            const locationEvaluation = await evaluateLocation(option.coordinates)

            return {
                ...unvalidatedAddress,
                coordinates: option.coordinates,
                isValid: locationEvaluation.deliverable,
                zone: locationEvaluation.deliverable ? locationEvaluation.zoneName : null,
            }
        }

        if (option.kind === 'suggestion' && option.isWhole === false) {
            return unvalidatedAddress
        }

        const validatedAddress = await geoApi.validateAddress(option.address, country)
        const locationEvaluation = await evaluateLocation(validatedAddress.coordinates)

        return {
            ...unvalidatedAddress,
            coordinates: validatedAddress.coordinates,
            isValid: locationEvaluation.deliverable,
            zone: locationEvaluation.deliverable ? locationEvaluation.zoneName : null,
        }
    }

    const selectAddressOption = async (option: CustomerLookupAddressOption) => {
        const resolutionId = ++addressResolutionIdRef.current

        onAddressFieldChange(createUnvalidatedAddress(option.address))
        setCurrentAddress({
            address: option.address,
            coordinates: option.coordinates ?? null,
        })
        setIsManualAddressSearch(false)

        try {
            const resolvedAddress = await resolveAddressSelection(option)

            if (resolutionId !== addressResolutionIdRef.current) return

            setAddressField(resolvedAddress, { shouldDirty: true, shouldValidate: true })
            setCurrentAddress({
                address: resolvedAddress.address,
                coordinates: resolvedAddress.coordinates,
            })
        } catch (error) {
            logger.errorOnly(error)

            if (resolutionId !== addressResolutionIdRef.current) return

            setAddressField(createUnvalidatedAddress(option.address), {
                shouldDirty: true,
                shouldValidate: true,
            })
        }
    }

    const handleCustomerAddressSelect = async (customer: CustomerLookupCustomer) => {
        const firstCustomerAddress = customer.addresses[0]

        if (!firstCustomerAddress) {
            addressResolutionIdRef.current += 1
            onAddressFieldChange(createUnvalidatedAddress(''))
            setCurrentAddress(undefined)
            setIsManualAddressSearch(false)
            triggerAddressField()
            return
        }

        const formattedAddress = formatCustomerAddress(firstCustomerAddress)

        if (!formattedAddress) {
            addressResolutionIdRef.current += 1
            onAddressFieldChange(createUnvalidatedAddress(''))
            setCurrentAddress(undefined)
            setIsManualAddressSearch(false)
            triggerAddressField()
            return
        }

        await selectAddressOption({
            address: formattedAddress,
            coordinates:
                firstCustomerAddress.lat != null && firstCustomerAddress.lng != null
                    ? {
                          latitude: firstCustomerAddress.lat,
                          longitude: firstCustomerAddress.lng,
                      }
                    : null,
            id: firstCustomerAddress.uuid,
            kind: 'customer',
        })
    }

    const handleAddressQueryChange = (value: string) => {
        addressResolutionIdRef.current += 1
        onAddressFieldChange(createUnvalidatedAddress(value))
        setIsManualAddressSearch(true)
        triggerAddressField()
    }

    const handleAddressSelect = async (addressId: string) => {
        const selectedAddressOption = addressOptions.find((option) => option.id === addressId)

        if (!selectedAddressOption) return

        await selectAddressOption(selectedAddressOption)
    }

    const resetAddressLookup = () => {
        addressResolutionIdRef.current += 1
        setCurrentAddress(undefined)
        setIsManualAddressSearch(false)
    }

    return {
        addressInputRef,
        addressQuery,
        addressSections,
        handleAddressQueryChange,
        handleAddressSelect,
        handleCustomerAddressSelect,
        hasAddressSearch,
        resetAddressLookup,
        selectedAddress,
    }
}
